import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { MailOutlined, MoreVertOutlined, SyncOutlined } from "@mui-symbols-material/w300"
import type { FC, ReactElement } from "react"
import React, { useRef, useState } from "react"

import { PermissionActionType } from "@/constants/permissions"
import { PriceChangeSubmissionStatus } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { RecipientType } from "@/screens/Pricing/constants"

interface PriceChangeHistoryRowActionProps {
  onRevert?: () => void
  onResendToAllCustomers?: () => void
  onResendToIndividual?: (recipientType: RecipientType) => void
  isRevertible: boolean
  status: PriceChangeSubmissionStatus
}

const PriceChangeHistoryRowAction: FC<PriceChangeHistoryRowActionProps> = ({
  onRevert,
  onResendToAllCustomers,
  onResendToIndividual,
  isRevertible,
  status,
}): ReactElement => {
  const [open, setOpen] = useState<boolean>(false)
  const anchorElRef = useRef<HTMLButtonElement>(null)

  const { hasPermissions } = usePermissionManager()
  const hasMaintenancePermission = hasPermissions([
    PermissionActionType.PRICING_MAINTENANCE,
    PermissionActionType.PRICING_MANAGER,
  ])

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation()
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }

  const handleRevertPriceChangeUpdates = (e: React.MouseEvent) => {
    e.stopPropagation()
    onRevert?.()
  }

  const handleResendIndividualCustomer = (e: React.MouseEvent) => {
    e.stopPropagation()
    onResendToIndividual?.(RecipientType.Customer)
    handleClose()
  }
  const handleResendIndividualPerson = (e: React.MouseEvent) => {
    e.stopPropagation()
    onResendToIndividual?.(RecipientType.Contact)
    handleClose()
  }

  const handleResendToAllCustomers = (e: React.MouseEvent) => {
    e.stopPropagation()
    onResendToAllCustomers?.()
    handleClose()
  }

  return (
    <>
      <IconButton aria-label='expand row' size='small' onClick={handleOpen} ref={anchorElRef}>
        <MoreVertOutlined />
      </IconButton>

      <Menu
        anchorEl={anchorElRef.current}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {status === PriceChangeSubmissionStatus.Approved && (
          <>
            <MenuItem
              onClick={handleResendToAllCustomers}
              className='cursor-pointer hover:bg-primary-100'
              disabled={!hasMaintenancePermission}
            >
              <ListItemIcon>
                <MailOutlined className='text-gray-600' />
              </ListItemIcon>
              <ListItemText primary='Resend to All Customers' className='text-gray-800' />
            </MenuItem>

            <MenuItem
              onClick={handleResendIndividualCustomer}
              className='cursor-pointer hover:bg-primary-100'
              disabled={!hasMaintenancePermission}
            >
              <ListItemIcon>
                <MailOutlined className='text-gray-600' />
              </ListItemIcon>
              <ListItemText primary='Resend to Individual Customer' className='text-gray-800' />
            </MenuItem>

            <MenuItem
              onClick={handleResendIndividualPerson}
              className='cursor-pointer hover:bg-primary-100'
              disabled={!hasMaintenancePermission}
            >
              <ListItemIcon>
                <MailOutlined className='text-gray-600' />
              </ListItemIcon>
              <ListItemText primary='Resend to Individual Person' className='text-gray-800' />
            </MenuItem>
          </>
        )}

        {status === PriceChangeSubmissionStatus.Approved && <Divider />}

        <MenuItem
          onClick={handleRevertPriceChangeUpdates}
          className='cursor-pointer hover:bg-primary-100'
          disabled={!hasMaintenancePermission || !isRevertible}
        >
          <ListItemIcon>
            <SyncOutlined className='text-gray-600' />
          </ListItemIcon>
          <ListItemText primary='Revert Price Change Update' className='text-gray-800' />
        </MenuItem>
      </Menu>
    </>
  )
}

export default PriceChangeHistoryRowAction
